import {useState, useMemo, useCallback, type RefObject} from 'react'
import {Typography, Box, Button, Drawer, IconButton, Tooltip, useMediaQuery} from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'
import LandscapeIcon from '@mui/icons-material/Landscape'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import AspectRatioIcon from '@mui/icons-material/AspectRatio'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {RenderExportFn} from '../../../common/components/ExportHandler'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'
import {setExportFramePreview} from '../../../common/components/exportFramePreviewStore'
import FreeScreenImagePanel from './FreeScreenImagePanel'
import FreeBackgroundPanel from './FreeBackgroundPanel'
import FreeDeviceSelectPanel from './FreeDeviceSelectPanel'
import FreeExportResolutionPanel from './FreeExportResolutionPanel'
import ExportDialog from '../rightSidebar/ExportDialog'
import {MOBILE_BREAKPOINT} from '../constants/responsive'

// ── Types ─────────────────────────────────────────────────────────

interface FreeLeftSidebarProps {
  renderExportRef: RefObject<RenderExportFn | null>
  onExportRendering?: (rendering: boolean) => void
  workerBusy?: boolean
}

// ── FreeLeftSidebar — flat single panel for the free editor ───────

const FreeLeftSidebar = ({renderExportRef, onExportRendering, workerBusy}: FreeLeftSidebarProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)

  const handleOpenExport = useCallback(() => {
    setDrawerOpen(false)
    setExportDialogOpen(true)
  }, [])

  const handleCloseExport = useCallback(() => {
    setExportDialogOpen(false)
  }, [])

  const handleOpenDrawer = useCallback(() => {
    setDrawerOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false)
  }, [])

  // Hover preview — shows the export frame while pointer is over the button.
  // No-op if the frame is already enabled.
  const handleExportButtonEnter = useCallback(() => {
    if (!exportFrameEnabled) setExportFramePreview(true)
  }, [exportFrameEnabled])

  const handleExportButtonLeave = useCallback(() => {
    setExportFramePreview(false)
  }, [])

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const panelContent = (
    <Box sx={sx.panel}>
      <Box sx={sx.scrollArea}>
        {/* Screen Image */}
        <Box sx={sx.sectionHeader}>
          <ImageIcon sx={sx.sectionIcon} />
          <Typography sx={sx.sectionTitle}>{t('leftSidebar.screenImage')}</Typography>
        </Box>
        <Box sx={sx.sectionContent}>
          <FreeScreenImagePanel />
        </Box>

        {/* Background */}
        <Box sx={sx.sectionHeader}>
          <LandscapeIcon sx={sx.sectionIcon} />
          <Typography sx={sx.sectionTitle}>{t('scene.background.title')}</Typography>
        </Box>
        <Box sx={sx.sectionContent}>
          <FreeBackgroundPanel />
        </Box>

        {/* Device Presets */}
        <Box sx={sx.sectionHeader}>
          <PhoneIphoneIcon sx={sx.sectionIcon} />
          <Typography sx={sx.sectionTitle}>{t('sidebar.device')}</Typography>
        </Box>
        <Box sx={sx.sectionContent}>
          <FreeDeviceSelectPanel />
        </Box>

        {/* Export resolution + frame toggle */}
        <Box sx={sx.sectionHeader}>
          <AspectRatioIcon sx={sx.sectionIcon} />
          <Typography sx={sx.sectionTitle}>{t('exportResolution.title')}</Typography>
        </Box>
        <Box sx={sx.sectionContent}>
          <FreeExportResolutionPanel />
        </Box>
      </Box>

      {/* Export button pinned at bottom */}
      <Box sx={sx.exportSection}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<FileDownloadIcon />}
          onClick={handleOpenExport}
          onMouseEnter={handleExportButtonEnter}
          onMouseLeave={handleExportButtonLeave}
          disabled={workerBusy}
          sx={{fontSize: 12, textTransform: 'none'}}
        >
          {t('exportDialog.title')}
        </Button>
      </Box>
    </Box>
  )

  return (
    <>
      {!isMobile && panelContent}
      {isMobile && (
        <>
          <Tooltip title={t('freeSidebar.openControls')} placement="right">
            <IconButton aria-label={t('freeSidebar.openControls')} onClick={handleOpenDrawer} sx={sx.burger}>
              <MenuRoundedIcon sx={sx.burgerIcon} />
            </IconButton>
          </Tooltip>
          <Drawer anchor="left" open={drawerOpen} onClose={handleCloseDrawer} slotProps={{paper: {sx: sx.drawerPaper}}}>
            {panelContent}
          </Drawer>
        </>
      )}
      <ExportDialog
        open={exportDialogOpen}
        onClose={handleCloseExport}
        renderExportRef={renderExportRef}
        onRenderingChange={onExportRendering}
        freeMode
      />
    </>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  panel: {
    width: tokens.sidebar.width,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: tokens.sidebar.background,
    borderRight: `1px solid ${tokens.sidebar.border}`,
    flexShrink: 0,
  },
  drawerPaper: {
    width: tokens.sidebar.width,
    backgroundColor: tokens.sidebar.background,
    backgroundImage: 'none',
  },
  burger: {
    position: 'absolute' as const,
    bottom: 'max(16px, calc(env(safe-area-inset-bottom) + 8px))',
    left: 16,
    zIndex: 25,
    width: 48,
    height: 48,
    borderRadius: '12px',
    color: '#ffffff',
    backgroundColor: tokens.accent.primary,
    border: `1px solid ${tokens.accent.dark}`,
    boxShadow: `0 4px 16px ${tokens.accent.glow}`,
    '&:hover': {
      backgroundColor: tokens.accent.light,
      borderColor: tokens.accent.primary,
    },
  },
  burgerIcon: {
    fontSize: 22,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
  },
  sectionIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.text.primary,
  },
  sectionContent: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
  exportSection: {
    borderTop: `1px solid ${tokens.accordion.border}`,
    padding: '12px 16px',
    flexShrink: 0,
  },
})

export default FreeLeftSidebar
